/**
 * Life4Earth V2 — React hook for loading schedule series data from the API.
 *
 * Replaces: import { scheduleProgressData } from './Data/scheduleprogressdata'
 * Returns schedule points in the seed-data shape the existing pipeline expects,
 * pre-indexed by project_GUID for direct use in App.tsx's Data computation.
 *
 * Includes fallback to seed data if the API is unreachable.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllScheduleSeriesPoints,
  schedulePointToSeedShape,
} from "./scheduleSeriesPoints";
import type { ScheduleSeriesPointResponse } from "./scheduleSeriesPoints";
import {
  fetchForKey,
  invalidateAndRefetch,
  CACHE_UPDATE_EVENT,
  QUERY_KEYS,
} from "./queryCache";

export interface UseApiScheduleDataResult {
  /** All schedule points in seed-data shape (project_GUID, reporting_date, etc.) */
  scheduleData: Record<string, any>[];
  /** Raw API responses (for components that want the new shape) */
  raw: ScheduleSeriesPointResponse[];
  /** True while the initial fetch is in progress */
  loading: boolean;
  /** Error message if the API call failed */
  error: string | null;
  /** Whether we're using API data or fell back to seed data */
  source: "api" | "seed" | "loading";
  /** Force a refetch from the API */
  refetch: () => void;
}

export function useApiScheduleData(
  fallbackFn?: () => any[]
): UseApiScheduleDataResult {
  const [scheduleData, setScheduleData] = useState<Record<string, any>[]>([]);
  const [raw, setRaw] = useState<ScheduleSeriesPointResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "seed" | "loading">("loading");
  const mountedRef = useRef(true);

  const refetch = useCallback(() => {
    invalidateAndRefetch(QUERY_KEYS.schedule);
  }, []);

  const load = useCallback(() => {
    setError(null);
    fetchForKey(
      QUERY_KEYS.schedule,
      async () => {
        const apiPoints = await fetchAllScheduleSeriesPoints();
        const transformed = apiPoints.map(schedulePointToSeedShape);
        return { raw: apiPoints, transformed };
      },
      {
        fallbackFn: fallbackFn
          ? () => {
              const seedData = fallbackFn();
              return { raw: [], transformed: seedData };
            }
          : undefined,
      }
    )
      .then((result) => {
        if (!mountedRef.current) return;
        setRaw(result.raw);
        setScheduleData(result.transformed);
        setSource("api");
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.warn(
          "[useApiScheduleData] API unreachable, falling back to seed data:",
          err.message
        );
        setError(err.message);
        setSource("seed");
        setLoading(false);
      });
  }, [fallbackFn]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Subscribe to cache invalidation
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === QUERY_KEYS.schedule) {
        load();
      }
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [load]);

  return { scheduleData, raw, loading, error, source, refetch };
}
